import { useMemo, useState } from "react";
import type { SessionContact } from "../protocol";

/** Create a #Group-chat: name it and invite ≥2 agent sessions to talk to each other (the human joins as the
 *  observer automatically). Groups are pairing-scoped, so the invitees must all belong to ONE pairing — the
 *  picker groups sessions by pairing and locks selection to the first-picked pairing. */
export function NewGroupModal({
  sessions,
  onClose,
  onCreate,
}: {
  sessions: SessionContact[];
  onClose: () => void;
  onCreate: (pairingId: string, title: string, agentSessionIds: string[]) => void;
}) {
  const [title, setTitle] = useState("");
  const [picked, setPicked] = useState<Set<string>>(new Set()); // sessionIds
  // The pairing the selection is locked to (the first pick's) — invitees must share one pairing.
  const [pairingId, setPairingId] = useState<string | null>(null);

  const byPairing = useMemo(() => {
    const m = new Map<string, SessionContact[]>();
    for (const s of sessions) {
      const list = m.get(s.pairingId) ?? [];
      list.push(s);
      m.set(s.pairingId, list);
    }
    return m;
  }, [sessions]);

  const toggle = (s: SessionContact) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(s.sessionId)) {
        next.delete(s.sessionId);
      } else {
        next.add(s.sessionId);
      }
      if (next.size === 0) setPairingId(null);
      else if (!pairingId) setPairingId(s.pairingId);
      return next;
    });
  };

  // The name is optional — when blank the group is labelled by its participants. Only the picks gate creation.
  const canCreate = picked.size >= 2 && pairingId !== null;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>New group</h3>
        <p className="muted">
          Invite agents to talk to each other — one can request work from another&apos;s repo and hear back when
          it&apos;s done. You join as the observer.
        </p>
        <input
          className="group-title-input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Group name (optional)"
          autoFocus
        />
        <div className="group-picker">
          {sessions.length === 0 ? (
            <p className="muted">No sessions to invite yet.</p>
          ) : (
            [...byPairing.entries()].map(([pid, list]) => (
              <div key={pid} className="group-picker-pairing">
                {byPairing.size > 1 && <div className="group-picker-head">Pairing {pid.slice(0, 6)}</div>}
                {list.map((s) => {
                  const checked = picked.has(s.sessionId);
                  // Once a pairing is locked in, sessions from other pairings can't be added to the same group.
                  const locked = pairingId !== null && s.pairingId !== pairingId && !checked;
                  return (
                    <label key={s.sessionId} className={`group-picker-row${locked ? " locked" : ""}`}>
                      <input type="checkbox" checked={checked} disabled={locked} onChange={() => toggle(s)} />
                      <span className="label">{s.label}</span>
                    </label>
                  );
                })}
              </div>
            ))
          )}
        </div>
        <div className="modal-actions">
          <button className="linkish" onClick={onClose}>
            Cancel
          </button>
          <button
            disabled={!canCreate}
            onClick={() => canCreate && onCreate(pairingId!, title, [...picked])}
          >
            Create
          </button>
        </div>
      </div>
    </div>
  );
}
